import { RouterRootState } from "connected-react-router";
import React, { FC, useState } from "react";
import { useSelector } from "react-redux";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { useLocale } from "../../contexts/localeContext";
import { useFilter } from "../../contexts/table/FilterContext";
import { useTableLegends } from "../../contexts/table/LegendContext";
import { usePagination } from "../../contexts/table/PaginationContext";
import { useSearch } from "../../contexts/table/SearchContext";
import { useSort } from "../../contexts/table/SortContext";
import { useTable } from "../../contexts/table/TableContext";
import ActivityFrame from "../activity-frame/activity-frame";
import FilterChips from "../filter-chips/filter-chips";
import FilterModal from "../filter-modal/filter-modal";
import Paginator from "../paginator/paginator";
import SearchBar from "../search-bar/search-bar";
import Table from "../table/table";
import TableLegend from "../table/table-legend/table-legend";
import { wrapName } from "../table/table-legend/table-state-wrapper";
import EmptyState from './../emptyTableState/EmptyTableState';

type Props = {
    hideAddNewButton?: boolean,
    title: string;
    selectedIds?: string[];
    setSelectedIds?: (selectedIds: string[]) => void;
    selectRows?: boolean;
    rowPath?: string;
    reportTitle?: string;
    customReportDescription?: string,
    onRowClicked?: (rowData: any) => void,
    setShowModal?: any,
    onTagSearchOpen?: () => void,
    isPaginationSupported?: boolean,
    eventThumbnails?: any,
    addNewButton?: any,
    noneFoundMessage?: string,
    customHighlightRow?: any,
    tableId?: string,
    showFilterMenu?: boolean,
    customRowColourWithRange?: any,
    disableFilterToggling?: boolean,
    style?: React.CSSProperties,
    isStriped?: boolean,
    tableHeight?: string,
    cyName: string,
    /**
     * This function is dependent on setSelectedIds being set, if is undefined this function will not be called.
     * This is so that we don't have alot of approach to manipulate the row which can be confusing. The function
     * is mainly to get the full row data of the selected rows.
     */
    setSelectedRows?: (rows: any[]) => void;
    selectedRows?: any[];
}
const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}
const TableTab: FC<Props> = ({ title,
    hideAddNewButton,
    addNewButton,
    setShowModal,
    selectedIds = [],
    setSelectedIds = () => { },
    selectRows = true,
    rowPath = "",
    reportTitle = "",
    customReportDescription = "",
    children,
    onRowClicked,
    onTagSearchOpen,
    isPaginationSupported = true,
    eventThumbnails,
    noneFoundMessage = undefined,
    customHighlightRow,
    tableId = undefined,
    showFilterMenu = false,
    customRowColourWithRange,
    disableFilterToggling = false,
    style,
    isStriped,
    setSelectedRows = undefined,
    cyName = title,
    tableHeight,
    selectedRows = undefined
}) => {
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));
    const legendContext = useTableLegends();
    const tableContext = useTable();
    const filterContext = useFilter();
    const searchContext = useSearch();
    const sortContext = useSort();
    const paginationContext = usePagination();
    const { translate } = useLocale();
    const [canShowEmptyState, setCanShowEmptyState] = useState<boolean>(false);

    setTimeout(() => setCanShowEmptyState(true), 2000);

    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filterModalTrigger, setFilterModalTrigger] = useState<string>('');

    const toggleFilterModal = (trigger: string) => {
        if (!disableFilterToggling) {
            setFilterModalTrigger(trigger);
            setShowFilterModal(!showFilterModal);
        }
    };

    const checkReport = (event) => {
        const checkReportTitle = reportTitle ? reportTitle : title;
        const checkCustomReportDescription = customReportDescription ? customReportDescription : title;
        tableContext.exportData(event, checkReportTitle, checkCustomReportDescription)
    }

    const onSelection = (ids) => {
        const newSelectedIds: string[] = selectedIds.map((id) => ids.includes(id) ? "" : id)
            .filter((id) => id.length > 0)
            .concat(ids.map((id) => selectedIds.includes(id) ? "" : id).filter((id) => id.length > 0))
        
        if (setSelectedRows && selectedRows) {
            const newRows = tableContext.tableData.filter((row) => newSelectedIds.includes(row.id.toString()));
            const rows = selectedRows.filter((row) => newSelectedIds.includes(row.id.toString()));
            newRows.forEach((row) => {
                if (row.id && !rows.find(r => r.id === row.id)) {
                    rows.push(row);
                }
            });

            setSelectedRows(rows);
        }
        
        setSelectedIds(newSelectedIds);
    }

    const getTableJSX = (): JSX.Element => {
        return <div className="flex overflow-y-hidden mb-3">
            <Table
                cyTableName={cyName}
                selectRows={selectRows}
                data={tableContext.tableData.map((row) => ({
                    ...row,
                    name: wrapName(
                        row,
                        { rowPath: { path: rowPath, routeName: routeName } },
                        legendContext.legendItems,
                        translate.byKey(TranslationKeys.defaultRecord)
                    ),
                }))}
                columns={tableContext.tableDescriptor}
                customise
                onSort={sortContext.updateDirection}
                selectedIds={selectedIds}
                onSelectionUpdated={onSelection}
                onRowClicked={onRowClicked}
                customHighlightRow={customHighlightRow}
                tableId={tableId}
                customRowColourWithRange={customRowColourWithRange}
                rowPath={rowPath}
                isStriped={isStriped}/>
        </div>
    }

    return (
        <div className="w-full flex flex-col" style={style ? style : {}}>
            <div className="flex flex-row w-full justify-between">
                <div className="w-full flex flex-col">
                    <div
                        className={`mb-3 text-portal-blue-primary-shade2 text-xl font-semibold`} >
                        {title}
                    </div>
                    {filterContext.filters.filter((filter) => filter.showFilterChips !== false).length > 0 ?
                        <div className="flex flex-row mb-3">
                            <span className="mr-2 text-sm">{translate.byKey(TranslationKeys.filters)}:</span>
                            <FilterChips
                                toggleFilterModal={toggleFilterModal}
                                filters={filterContext.filters}
                                disableFilterToggling={disableFilterToggling}
                            >
                            </FilterChips>
                        </div>
                        : null}
                    <SearchBar
                        cyName={cyName}
                        newSearchTerm={searchContext.searchTerm}
                        setSearchTerm={searchContext.updateSearchTerm}
                        setSearchColumn={searchContext.updateSearchColumn}
                        toggleFilterModal={toggleFilterModal}
                        columns={searchContext.searchDescriptor}
                        generateReport={(event) => checkReport(event)}
                        onTagSearchOpen={onTagSearchOpen}
                        showFilterMenu={showFilterMenu}>
                        {children}
                    </SearchBar>
                </div>
                <div className="flex justify-end">
                    {eventThumbnails}
                </div>
            </div>
            <ActivityFrame busy={tableContext.busy} className={`w-full ${tableContext.busy ? 'mt-40' : 'h-full overflow-y-auto'}`} tableHeight={tableHeight ? tableHeight : ''}>
                {tableContext.tableData.length === 0 && canShowEmptyState ?
                    <div style={{ height: '30rem', }}>
                        <EmptyState
                            message={noneFoundMessage}
                            addNewButton={addNewButton}
                            hideAddNewButton={hideAddNewButton}
                            rowPath={rowPath}
                            setShowModal={setShowModal} />
                    </div>

                    : isPaginationSupported ? <Paginator
                        itemCount={tableContext.tableData.length}
                        paginationObj={paginationContext.pagination}
                        onPageNumberUpdated={paginationContext.updatePageNumber}
                        onPageSizeUpdated={paginationContext.updatePageSize}
                        sizes={paginationContext.pagination.sizes}>
                        {getTableJSX}
                    </Paginator>
                        : getTableJSX()
                }

            </ActivityFrame>
            {canShowEmptyState && tableContext.tableData.length === 0 && !legendContext.tableLegendSelectable ? null : <TableLegend />}

            <FilterModal
                isOpen={showFilterModal}
                onClose={() => { setShowFilterModal(false) }}
                filterModalTrigger={filterModalTrigger} />
        </div>
    );
};

export default TableTab;